import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import ManageCategoryModal from "../../modal/ManageCategory"
import NoExperiencesDeleteDeactivateModal from "../../modal/NoexperiencesDeleteDeactivate"
import DeleteDeactivateModal from "../../modal/DeactivateDelete"
import { EQUIPMENT_DELETE_DEACTIVATE_MODES } from "../../constants"

/**
 * Container component for all equipment page modals
 * Separates modal rendering logic from main page component
 */
const EquipmentModals = ({
  categoryModalOpen,
  onCategoryModalClose,
  deactivateDeleteModalState,
  onDeactivateDeleteModalClose,
  onDeactivateDeleteModalOk,
  loading = false,
  detailsLoading = false
}) => {
  const { t } = useTranslation()

  const equipmentTitle = deactivateDeleteModalState?.equipment?.title || ""
  const mode = deactivateDeleteModalState?.mode
  const details = deactivateDeleteModalState?.details || {}
  const action = deactivateDeleteModalState?.action

  // Get messages from API response
  const expMessage = details.exp_message || null
  const bookingMessage = details.booking_message || null

  // Get warning message - for backward compatibility with old 'message' key
  const getWarningMessage = () => {
    // For partially substitutable, we'll pass messages separately to the modal
    // so it can display them in appropriate sections
    if (mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE) {
      return null // Messages will be passed via expMessage and bookingMessage props
    }

    // Use exp_message or booking_message if available
    if (expMessage) return expMessage
    if (bookingMessage) return bookingMessage

    // Fallback to translation keys
    if (action === "deactivate") {
      return t("equipment.modals.warning_deactivate_remove", { name: equipmentTitle })
    }
    return t("equipment.modals.warning_delete_remove", { name: equipmentTitle })
  }

  // Generate mode-specific subtitle
  const equipmentName = details.boat_name || equipmentTitle
  const assignedTripsCount = details.assigned_trips?.length || 0
  const activeBookingsCount = details.active_bookings?.length || 0

  let subtitle = null

  // Mode-specific subtitles for deactivate action
  if (action === "deactivate") {
    switch (mode) {
      case EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
        subtitle = (
          <>
            <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} {assignedTripsCount} {t("equipment.modals.experiences")}:
          </>
        )
        break
      case EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
      case EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
        subtitle = (
          <>
            <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} ({activeBookingsCount}) {t("equipment.modals.upcoming_trips")}. {t("equipment.modals.to_deactivate")} <strong>{equipmentName}</strong> {t("equipment.modals.you_must_replace_it")}.
          </>
        )
        break
      default:
        subtitle = (
          <>
            {t("equipment.modals.deactivate_equipment_description_prefix")}
            <strong>{equipmentName}</strong>
            {t("equipment.modals.deactivate_equipment_description_suffix")}
          </>
        )
    }
  }

  // Mode-specific subtitles for delete action
  if (action === "delete") {
    switch (mode) {
      case EQUIPMENT_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
        subtitle = (
          <>
            <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} {assignedTripsCount} {t("equipment.modals.experiences")}:
          </>
        )
        break
      case EQUIPMENT_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
      case EQUIPMENT_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
        subtitle = (
          <>
            <strong>{equipmentName}</strong> {t("equipment.modals.is_assigned_to")} ({activeBookingsCount}) {t("equipment.modals.upcoming_trips")}. {t("equipment.modals.to_delete")} <strong>{equipmentName}</strong> {t("equipment.modals.you_must_replace_it")}.
          </>
        )
        break
      default:
        subtitle = (
          <>
            {t("equipment.modals.delete_equipment_description_prefix")}
            <strong>{equipmentName}</strong>
            {t("equipment.modals.delete_equipment_description_suffix")}
          </>
        )
    }
  }

  // Render NO_EXPERIENCES modal separately
  const isNoExperiencesMode = mode === EQUIPMENT_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES

  return (
    <>
      {categoryModalOpen && (
        <ManageCategoryModal open={categoryModalOpen} onCancel={onCategoryModalClose} onUpdate={onCategoryModalClose} />
      )}
      {/* Render appropriate modal based on mode */}
      {deactivateDeleteModalState?.visible && isNoExperiencesMode && (
        <NoExperiencesDeleteDeactivateModal
          open={deactivateDeleteModalState.visible}
          title={
            deactivateDeleteModalState.action === "deactivate"
              ? t("equipment.modals.deactivate_equipment_title")
              : t("equipment.modals.delete_equipment_title")
          }
          description={
            deactivateDeleteModalState.action === "deactivate"
              ? t("equipment.modals.deactivate_equipment_description", { title: equipmentTitle })
              : t("equipment.modals.delete_equipment_description", { title: equipmentTitle })
          }
          onCancel={onDeactivateDeleteModalClose}
          onOk={onDeactivateDeleteModalOk}
          okText={deactivateDeleteModalState.action === "deactivate" ? t("global.deactivate") : t("global.delete")}
          cancelText={t("global.cancel")}
          loading={loading}
        />
      )}
      {deactivateDeleteModalState?.visible && !isNoExperiencesMode && mode && (
        <DeleteDeactivateModal
          visible={deactivateDeleteModalState.visible}
          onCancel={onDeactivateDeleteModalClose}
          onOk={onDeactivateDeleteModalOk}
          title={
            deactivateDeleteModalState.action === "deactivate"
              ? t("equipment.modals.deactivate_equipment_title")
              : t("equipment.modals.delete_equipment_title")
          }
          subtitle={subtitle}
          okText={deactivateDeleteModalState.action === "deactivate" ? t("global.deactivate") : t("global.delete")}
          cancelText={t("global.cancel")}
          loading={loading || detailsLoading}
          mode={mode}
          list={details.assigned_trips || []}
          upcomingBookings={details.active_bookings || []}
          assignedExperiences={details.assigned_trips || []}
          warning={getWarningMessage()}
          expMessage={expMessage}
          bookingMessage={bookingMessage}
        />
      )}
    </>
  )
}

EquipmentModals.propTypes = {
  categoryModalOpen: PropTypes.bool.isRequired,
  onCategoryModalClose: PropTypes.func.isRequired,
  deactivateDeleteModalState: PropTypes.shape({
    visible: PropTypes.bool.isRequired,
    action: PropTypes.oneOf(["deactivate", "delete"]),
    equipment: PropTypes.object,
    mode: PropTypes.string,
    details: PropTypes.object
  }),
  onDeactivateDeleteModalClose: PropTypes.func.isRequired,
  onDeactivateDeleteModalOk: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  detailsLoading: PropTypes.bool
}

export default React.memo(EquipmentModals)
